import React from "react"
import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible"
import { cn } from "../utils"
import { CollapsibleChevron } from "./collapsible-chevron"

// =============================================================================
// Styles
// =============================================================================
// Lets `Collapsible.Chevron` turn with the open state, on the default trigger
// and on any element passed as `render`
const groupStyles = "group/collapsible-trigger"

const triggerStyles = [
  "flex w-full items-center gap-2 py-3 pl-4 pr-3", // box
  "font-sans text-base leading-6 font-semibold text-gray-900", // typography
  "transition-colors ease-out not-data-disabled:hover:text-gray-800", // hover
  "rounded outline-2 -outline-offset-2 outline-transparent focus-visible:outline-gray-900",
  "data-disabled:cursor-not-allowed data-disabled:text-gray-600"
]

const labelStyles = "flex min-w-0 flex-1 items-center gap-2 text-left"

// =============================================================================
// Props
// =============================================================================
export type CollapsibleTriggerProps = {
  /**
   * The label. The default trigger adds a chevron after it; a custom trigger
   * passed as `render` places its own `Collapsible.Chevron`.
   */
  children: React.ReactNode
  /** Additional CSS classes, such as a border or spacing around the trigger */
  className?: string
} & Omit<CollapsiblePrimitive.Trigger.Props, "children" | "className">

// =============================================================================
// Component
// =============================================================================
/**
 * The full-width section header that opens and closes the content. Base UI
 * keeps `aria-expanded` and `aria-controls` in sync with the open state.
 *
 * Pass an element as `render`, such as a `Button`, to replace the header. Its
 * look then comes from that element, and `Collapsible.Chevron` goes inside it.
 * Set `nativeButton={false}` when the element isn't a `<button>`.
 */
export function CollapsibleTrigger({
  children,
  className,
  render,
  ...props
}: CollapsibleTriggerProps) {
  if (render) {
    return (
      <CollapsiblePrimitive.Trigger
        data-slot="collapsible-trigger"
        render={render}
        className={cn(groupStyles, className)}
        {...props}
      >
        {children}
      </CollapsiblePrimitive.Trigger>
    )
  }

  return (
    <CollapsiblePrimitive.Trigger
      data-slot="collapsible-trigger"
      className={cn(groupStyles, triggerStyles, className)}
      {...props}
    >
      <span className={labelStyles}>{children}</span>
      <CollapsibleChevron />
    </CollapsiblePrimitive.Trigger>
  )
}

CollapsibleTrigger.displayName = "Collapsible.Trigger"
